import { api, type ApiSchema } from "@/modules/api";

export type RoutineShareLink = ApiSchema["CoreApi__RoutineShareLinkResponseSchema"];
export type RoutineSharePreview = ApiSchema["CoreApi__RoutineSharePreviewResponseSchema"];
export type AcceptRoutineShareLinkResponse =
  ApiSchema["CoreApi__AcceptRoutineShareLinkResponseSchema"];

export const createRoutineShareLink = async ({
  sourceRoutineId,
}: {
  sourceRoutineId: string;
}): Promise<RoutineShareLink> => {
  const response = await api.post("/v2/routine-share-links", {
    body: {
      source_routine_id: sourceRoutineId,
    },
  });

  if (response.error || !response.data) {
    throw new Error("Unable to create routine share link");
  }

  return response.data;
};

// Loads the owner's reusable share link so the sharing modal can copy or revoke it.
export const getActiveRoutineShareLinkForSourceRoutine = async ({
  sourceRoutineId,
}: {
  sourceRoutineId: string;
}): Promise<RoutineShareLink | null> => {
  const response = await api.get("/v2/routine-share-links/by-source-routine/{source_routine_id}", {
    params: {
      path: {
        source_routine_id: sourceRoutineId,
      },
    },
  });

  if (response.response?.status === 404) {
    return null;
  }

  if (response.error || !response.data) {
    throw new Error("Unable to load routine share link");
  }

  return response.data;
};

export const previewRoutineShareLink = async ({
  verificationToken,
}: {
  verificationToken: string;
}): Promise<RoutineSharePreview> => {
  const response = await api.get("/v2/routine-share-links/{verification_token}/preview", {
    params: {
      path: {
        verification_token: verificationToken,
      },
    },
  });

  if (response.error || !response.data) {
    throw new Error("Unable to load routine");
  }

  return response.data;
};

export const acceptRoutineShareLink = async ({
  verificationToken,
}: {
  verificationToken: string;
}): Promise<AcceptRoutineShareLinkResponse> => {
  const response = await api.post("/v2/routine-share-links/{verification_token}/accept", {
    params: {
      path: {
        verification_token: verificationToken,
      },
    },
  });

  if (response.error || !response.data) {
    throw new Error("Unable to accept routine");
  }

  return response.data;
};

export const revokeRoutineShareLink = async ({ linkId }: { linkId: string }): Promise<void> => {
  const response = await api.post("/v2/routine-share-links/by-id/{link_id}/revoke", {
    params: {
      path: {
        link_id: linkId,
      },
    },
  });

  if (response.error || !response.data) {
    throw new Error("Unable to revoke routine share link");
  }
};
